import React, { memo, useMemo, useCallback } from 'react'
import { motion, useReducedMotion } from 'framer-motion'

// Mobile-optimized motion component that reduces animations on low-end devices
export const MobileMotion = memo(({ 
  children, 
  initial, 
  animate, 
  whileInView, 
  transition,
  viewport,
  className,
  style,
  ...props 
}: any) => {
  const shouldReduceMotion = useReducedMotion()
  
  // Simplify animations for mobile
  const mobileOptimizedProps = useMemo(() => {
    if (shouldReduceMotion) {
      return {
        initial: false,
        animate: false,
        whileInView: false,
        transition: { duration: 0 }
      }
    }
    
    return {
      initial,
      animate,
      whileInView,
      transition: {
        ...transition,
        // Reduce animation duration on mobile
        duration: (transition?.duration || 0.6) * 0.7
      },
      viewport: {
        ...viewport,
        // Optimize intersection observer
        margin: "0px 0px -10% 0px"
      }
    }
  }, [shouldReduceMotion, initial, animate, whileInView, transition, viewport])

  return (
    <motion.div
      {...mobileOptimizedProps}
      className={className}
      style={{
        ...style,
        // Add hardware acceleration
        willChange: 'transform',
        transform: 'translateZ(0)'
      }}
      {...props}
    >
      {children}
    </motion.div>
  )
})

MobileMotion.displayName = 'MobileMotion'

// Optimized gradient text component
export const OptimizedGradientText = memo(({ 
  children, 
  className, 
  gradient 
}: {
  children: React.ReactNode
  className?: string
  gradient: string
}) => {
  return (
    <span 
      className={`${className} bg-clip-text text-transparent`}
      style={{
        background: gradient,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        // Hardware acceleration for gradient text
        willChange: 'transform',
        transform: 'translateZ(0)'
      }}
    >
      {children}
    </span>
  )
})

OptimizedGradientText.displayName = 'OptimizedGradientText'

// Lazy loading wrapper for heavy sections
export const LazySection = memo(({ 
  children, 
  threshold = 0.1 
}: {
  children: React.ReactNode
  threshold?: number
}) => {
  const [isVisible, setIsVisible] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [threshold])

  return (
    <div ref={ref}>
      {isVisible ? children : <div style={{ height: '50vh' }} />}
    </div>
  )
})

LazySection.displayName = 'LazySection'

// Optimized button component for mobile
export const MobileOptimizedButton = memo(({
  children,
  className,
  style,
  gradient,
  onClick,
  disabled,
  type,
  ...motionProps
}: {
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
  gradient?: string
  onClick?: () => void
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
} & Omit<React.ComponentProps<typeof motion.button>, 'onClick' | 'className' | 'style' | 'disabled' | 'type'>) => {
  
  const handleClick = useCallback((e: React.MouseEvent) => {
    // Add haptic feedback for mobile
    if ('vibrate' in navigator) {
      navigator.vibrate(10)
    }
    onClick?.()
  }, [onClick])

  return (
    <motion.button
      className={className}
      style={{
        ...style,
        background: gradient,
        // Optimize for touch
        touchAction: 'manipulation',
        // Hardware acceleration
        willChange: 'transform',
        transform: 'translateZ(0)',
        // Prevent zoom on double tap
        userSelect: 'none'
      }}
      onClick={handleClick}
      disabled={disabled}
      type={type}
      whileTap={{ scale: 0.95 }}
      transition={{ duration: 0.1 }}
      {...motionProps}
    >
      {children}
    </motion.button>
  )
})

MobileOptimizedButton.displayName = 'MobileOptimizedButton'

// Performance monitoring hook
export const usePerformanceMonitor = () => {
  React.useEffect(() => {
    if (typeof window !== 'undefined' && 'performance' in window) {
      // Monitor long tasks
      if ('PerformanceObserver' in window) {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration > 50) {
              console.warn('Long task detected:', entry.duration + 'ms')
            }
          }
        })
        
        try {
          observer.observe({ entryTypes: ['longtask'] })
        } catch (e) {
          // Ignore if longtask is not supported
        }

        return () => observer.disconnect()
      }
    }
  }, [])
}

// Device capability detection
export const useDeviceCapabilities = () => {
  return useMemo(() => {
    if (typeof window === 'undefined') {
      return { isLowEnd: false, supportsWebGL: false, hasHardwareAcceleration: false }
    }

    // Detect low-end devices
    const isLowEnd = 
      (navigator as any).hardwareConcurrency <= 2 ||
      (navigator as any).deviceMemory <= 2 ||
      window.innerWidth <= 375

    // Check WebGL support
    const canvas = document.createElement('canvas')
    const supportsWebGL = !!(
      canvas.getContext('webgl') || 
      canvas.getContext('experimental-webgl')
    )

    // Check hardware acceleration
    const testElement = document.createElement('div')
    testElement.style.transform = 'translateZ(0)'
    const hasHardwareAcceleration = testElement.style.transform !== ''

    return {
      isLowEnd,
      supportsWebGL,
      hasHardwareAcceleration
    }
  }, [])
}